/*
 * Renderer: datepicker  –  type="datepicker"
 *
 * An editable date field (DD.MM.YYYY format) with a calendar dropdown.
 * The user can type a date directly into the input or click the calendar icon to pick one.
 * Fires `input-change` (bubbles, composed) with detail { value: "YYYY-MM-DD" }
 * after a valid typed date or after selecting a day in the calendar.
 * Use getValue() / setValue("YYYY-MM-DD" | null) to read or set the date programmatically.
 *
 * Attributes:
 *   min         – earliest selectable date (YYYY-MM-DD); earlier days are disabled
 *   max         – latest selectable date (YYYY-MM-DD); later days are disabled
 *   no-clear    – hides the "Löschen" (clear) button in the calendar footer
 *   placeholder – shown in the text field when empty (default "TT.MM.JJJJ")
 *   disabled    – disables typing and calendar interaction
 */
import { formatDateISO, fmtDE, parseDE } from '../utils/dateUtils.js';

export const DatepickerRenderer = {
    renderDatepicker() {
        this.clearShadow();

        if (!this.calViewDate) {
            const now = new Date();
            this.calViewDate = new Date(now.getFullYear(), now.getMonth(), 1);
        }

        const wrap = document.createElement('div');
        wrap.className = 'inputEditableTrigger';
        if (this.hasAttribute('disabled')) wrap.classList.add('disabled');

        const input = document.createElement('input');
        input.type        = 'text';
        input.className   = 'inputEditableInput';
        input.placeholder = this.getAttribute('placeholder') || 'TT.MM.JJJJ';
        input.maxLength   = 10;
        input.value       = this.value ? fmtDE(this.value) : '';
        input.disabled    = this.hasAttribute('disabled');

        const icon = document.createElement('i');
        icon.className = 'fa-solid fa-calendar inputEditableIcon';
        icon.setAttribute('aria-hidden', 'true');

        wrap.appendChild(input);
        wrap.appendChild(icon);

        const panel = document.createElement('div');
        panel.className = 'inputPanel calPanel';
        panel.hidden    = true;

        this.trigger   = wrap;
        this.panel     = panel;
        this.dateInput = input;
        this.buildCalendar();

        input.addEventListener('input', () => {
            const iso = parseDE(input.value);
            if (!iso) return;
            const min = this.getAttribute('min');
            const max = this.getAttribute('max');
            if (min && iso < min) return;
            if (max && iso > max) return;
            this.value = iso;
            const [y, m] = iso.split('-').map(Number);
            this.calViewDate = new Date(y, m - 1, 1);
            this.buildCalendar();
            this.dispatchEvent(new CustomEvent('input-change', {
                bubbles: true, composed: true, detail: { value: iso },
            }));
        });

        wrap.addEventListener('click', e => {
            if (e.target === input) return;
            e.stopPropagation();
            this.togglePanel();
        });

        input.addEventListener('keydown', e => {
            if (e.key === 'Escape')    this.closePanel();
            if (e.key === 'ArrowDown') { e.preventDefault(); this.openPanel(); }
        });

        this.shadowRoot.appendChild(wrap);
        this.shadowRoot.appendChild(panel);
        this.attachOutsideHandler();
    },

    buildCalendar() {
        const panel = this.panel;
        if (!panel) return;
        panel.innerHTML = '';

        const viewDate = this.calViewDate;
        const minDate = this.getAttribute('min') || null;
        const maxDate = this.getAttribute('max') || null;

        const prevMonthLastDay  = formatDateISO(new Date(viewDate.getFullYear(), viewDate.getMonth(), 0));
        const nextMonthFirstDay = formatDateISO(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
        const prevBlocked = minDate && prevMonthLastDay < minDate;
        const nextBlocked = maxDate && nextMonthFirstDay > maxDate;

        const goPrev = prevBlocked ? null : () => {
            this.calViewDate = new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1);
            this.buildCalendar();
            this.focusDateDay();
        };
        const goNext = nextBlocked ? null : () => {
            this.calViewDate = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);
            this.buildCalendar();
            this.focusDateDay();
        };

        panel.appendChild(this.buildCalHeader(viewDate, goPrev, goNext));
        panel.appendChild(this.buildCalGrid(viewDate, (dateStr) => {
            this.value = dateStr;
            if (this.dateInput) this.dateInput.value = fmtDE(dateStr);
            this.closePanel();
            this.dateInput?.focus();
            this.dispatchEvent(new CustomEvent('input-change', {
                bubbles: true, composed: true, detail: { value: dateStr },
            }));
            this.buildCalendar();
        }, { selected: this.value }, { goPrev, goNext }));

        const footer = document.createElement('div');
        footer.className = 'calFooter';

        if (!this.hasAttribute('no-clear')) {
            const clearBtn = document.createElement('button');
            clearBtn.className   = 'inputBtn calClearBtn';
            clearBtn.type        = 'button';
            clearBtn.textContent = 'Löschen';
            clearBtn.addEventListener('click', e => {
                e.stopPropagation();
                this.setValue(null);
                this.closePanel();
                this.dateInput?.focus();
            });
            clearBtn.addEventListener('keydown', e => {
                if (e.key === 'Escape') { e.stopPropagation(); this.closePanel(); this.dateInput?.focus(); }
            });
            footer.appendChild(clearBtn);
        }

        const todayBtn = document.createElement('button');
        todayBtn.className   = 'inputBtn calTodayBtn';
        todayBtn.type        = 'button';
        todayBtn.textContent = 'Heute';
        todayBtn.addEventListener('click', e => {
            e.stopPropagation();
            const today = formatDateISO(new Date());
            this.value       = today;
            this.calViewDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
            if (this.dateInput) this.dateInput.value = fmtDE(today);
            this.closePanel();
            this.dateInput?.focus();
            this.dispatchEvent(new CustomEvent('input-change', {
                bubbles: true, composed: true, detail: { value: today },
            }));
        });
        todayBtn.addEventListener('keydown', e => {
            if (e.key === 'Escape') { e.stopPropagation(); this.closePanel(); this.dateInput?.focus(); }
        });

        footer.appendChild(todayBtn);
        panel.appendChild(footer);
    },

    focusDateDay() {
        if (!this.isOpen) return;
        requestAnimationFrame(() => {
            const sel = this.value
                ? this.panel?.querySelector(`.calDay[data-date="${this.value}"]`)
                : null;
            const target = sel
                || this.panel?.querySelector('.calDay.calToday')
                || this.panel?.querySelector('.calDay:not(.calDayEmpty)');
            target?.focus();
        });
    },
};
